import {
	listAllEquipmentForOffline,
	listAllPartsForOffline,
} from "@packages/api-client/client/equipmentController";
import {
	listFinancialCategories,
	listVendors,
} from "@packages/api-client/client/financialHubController";
import {
	listAllAnimalsForOffline,
	listAllHerdsForOffline,
	listLivestockVaccinationRules,
} from "@packages/api-client/client/livestockController";
import { listAllPaddocksForOffline } from "@packages/api-client/client/paddockController";
import { listAllTasksForOffline } from "@packages/api-client/client/planningTasksController";
import { apiClientFetch } from "@packages/api-client/client-config";
import {
	listAllEquipmentForOfflineQueryKey,
	listAllPartsForOfflineQueryKey,
} from "@packages/api-client/hooks/equipmentController";
import {
	listAllAnimalsForOfflineQueryKey,
	listAllHerdsForOfflineQueryKey,
} from "@packages/api-client/hooks/livestockController";
import { listAllPaddocksForOfflineQueryKey } from "@packages/api-client/hooks/paddockController";
import { listAllTasksForOfflineQueryKey } from "@packages/api-client/hooks/planningTasksController";
import type {
	ListFinancialCategories200,
	ListVendors200,
} from "@packages/api-client/types/financialHubController";
import type { ListLivestockVaccinationRules200 } from "@packages/api-client/types/livestockController";

export type DatasetId =
	| "animals"
	| "herds"
	| "paddocks"
	| "equipment"
	| "parts"
	| "tasks"
	| "financialCategories"
	| "vendors"
	| "vaccinationRules";

/** Rows never exceed this before the offline payload is dropped as over-cap. */
const OFFLINE_ROW_CAP = 10_000;

/**
 * Query keys for the financial reference datasets prepared for the offline quick
 * "Lançamento" capture. Unlike the livestock datasets (backed by dedicated
 * `listAll*ForOffline` endpoints), categories and vendors reuse their regular —
 * already non-paginated — list endpoints, wrapped here into the `{ overCap, data }`
 * envelope `prepareDataset` expects. The quick form reads these keys straight
 * from the cache when offline, so they must stay distinct from the online list
 * keys (which hold a bare array). Exported so the consuming hook/combobox and the
 * registry agree on one key.
 */
export const financialCategoriesOfflineQueryKey = (organizationId: string) =>
	["offline", "financial-categories", organizationId] as const;

export const financialVendorsOfflineQueryKey = (organizationId: string) =>
	["offline", "financial-vendors", organizationId] as const;

/** Shape stored under the offline financial-dataset keys after preparation. */
export type OfflineFinancialCategories = {
	overCap: boolean;
	data: ListFinancialCategories200;
};
export type OfflineVendors = { overCap: boolean; data: ListVendors200 };

/**
 * The org's sanitary-calendar rules, prepared for the offline Vacinação form.
 * Distinct from `listLivestockVaccinationRulesQueryKey`, which holds a bare array.
 */
export const vaccinationRulesOfflineQueryKey = (organizationId: string) =>
	["offline", "vaccination-rules", organizationId] as const;

export type OfflineVaccinationRules = {
	overCap: boolean;
	data: ListLivestockVaccinationRules200;
};

export type DatasetScope = {
	organizationId: string;
	establishmentId?: string;
};

export type DatasetDef = {
	id: DatasetId;
	labelKey: string;
	/**
	 * Organization-wide datasets (no farm dimension). The Offline Center prepares
	 * these once under the org scope instead of fanning out per establishment —
	 * the query ignores `establishmentId`, so a per-farm fan-out would refetch the
	 * same list and write redundant per-farm meta.
	 */
	orgScoped?: boolean;
	queryOptionsFactory: (scope: DatasetScope) => {
		queryKey: readonly unknown[];
		queryFn: () => Promise<unknown>;
		staleTime: number;
		gcTime: number;
	};
};

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

/**
 * Builds an org-wide offline dataset from a regular (already non-paginated) list
 * endpoint, wrapping the bare array in the `{ overCap, data }` envelope
 * `prepareDataset` expects. Shared by the org-wide datasets so the cap logic and
 * cache shape live in one place.
 */
function orgWideListDataset<T>(opts: {
	id: DatasetId;
	labelKey: string;
	queryKey: (organizationId: string) => readonly unknown[];
	fetchAll: (organizationId: string) => Promise<T[]>;
}): DatasetDef {
	return {
		id: opts.id,
		labelKey: opts.labelKey,
		orgScoped: true,
		queryOptionsFactory: (scope) => ({
			queryKey: opts.queryKey(scope.organizationId),
			queryFn: async (): Promise<{ overCap: boolean; data: T[] }> => {
				const data = await opts.fetchAll(scope.organizationId);
				return data.length > OFFLINE_ROW_CAP
					? { overCap: true, data: [] }
					: { overCap: false, data };
			},
			staleTime: 0,
			gcTime: FIVE_DAYS_MS,
		}),
	};
}

const animalsDataset: DatasetDef = {
	id: "animals",
	labelKey: "offline.center.datasets.animals",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllAnimalsForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllAnimalsForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

const herdsDataset: DatasetDef = {
	id: "herds",
	labelKey: "offline.center.datasets.herds",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllHerdsForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllHerdsForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

const paddocksDataset: DatasetDef = {
	id: "paddocks",
	labelKey: "offline.center.datasets.paddocks",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllPaddocksForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllPaddocksForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

const equipmentDataset: DatasetDef = {
	id: "equipment",
	labelKey: "offline.center.datasets.equipment",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllEquipmentForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllEquipmentForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

const partsDataset: DatasetDef = {
	id: "parts",
	labelKey: "offline.center.datasets.parts",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllPartsForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllPartsForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

const tasksDataset: DatasetDef = {
	id: "tasks",
	labelKey: "offline.center.datasets.tasks",
	queryOptionsFactory: (scope) => ({
		queryKey: listAllTasksForOfflineQueryKey(scope.organizationId, {
			establishmentId: scope.establishmentId,
		}),
		queryFn: () =>
			listAllTasksForOffline(
				scope.organizationId,
				{ establishmentId: scope.establishmentId },
				{ client: apiClientFetch },
			),
		staleTime: 0,
		gcTime: 5 * 24 * 60 * 60 * 1000,
	}),
};

// Categories, vendors and vaccination rules are organization-wide (no farm
// scope): each fetches the full non-paginated list and keys only on the org.
const financialCategoriesDataset = orgWideListDataset({
	id: "financialCategories",
	labelKey: "offline.center.datasets.financialCategories",
	queryKey: financialCategoriesOfflineQueryKey,
	fetchAll: (orgId) =>
		listFinancialCategories(orgId, {}, { client: apiClientFetch }),
});

const vendorsDataset = orgWideListDataset({
	id: "vendors",
	labelKey: "offline.center.datasets.vendors",
	queryKey: financialVendorsOfflineQueryKey,
	fetchAll: (orgId) => listVendors(orgId, {}, { client: apiClientFetch }),
});

const vaccinationRulesDataset = orgWideListDataset({
	id: "vaccinationRules",
	labelKey: "offline.center.datasets.vaccinationRules",
	queryKey: vaccinationRulesOfflineQueryKey,
	fetchAll: (orgId) =>
		listLivestockVaccinationRules(orgId, { client: apiClientFetch }),
});

export const DATASET_REGISTRY: Record<DatasetId, DatasetDef> = {
	animals: animalsDataset,
	herds: herdsDataset,
	paddocks: paddocksDataset,
	equipment: equipmentDataset,
	parts: partsDataset,
	tasks: tasksDataset,
	financialCategories: financialCategoriesDataset,
	vendors: vendorsDataset,
	vaccinationRules: vaccinationRulesDataset,
};
